/**
 * DeviceRemoteApi — appels du terminal vers le module Assistance (super_admin).
 *
 * - pullCommands : commandes en attente pour CE terminal. `idle` = caisse inactive ;
 *   caisse occupée, le serveur ne livre que ping et capture.
 * - ackCommand   : accusé (ack / failed) avec le résultat, tracé au journal d'assistance.
 * - isAutoUpdateLocked : kill switch `updates.autoupdate`.
 *
 * Tant que la migration 20261102210000 n'est pas en prod, la signature à 3 arguments
 * n'existe pas (PGRST202) : repli sur l'ancienne, appelée seulement caisse inactive
 * puisqu'elle livre alors toutes les commandes.
 */

import { supabase } from '@/integrations/supabase/client';
import { appErrorFromSupabase, type AppError } from '@/api/errors';
import { err, ok, type Result } from '@/api/result';
import type { Json } from '@/integrations/supabase/types';

export type PulledCommand = {
  id: string;
  command_type: string;
  payload: unknown;
  created_at: string;
  expires_at: string;
};

const FUNCTION_NOT_FOUND = 'PGRST202';

async function pullLegacy(
  deviceId: string,
  tenantId: string,
  idle: boolean,
): Promise<Result<PulledCommand[], AppError>> {
  if (!idle) return ok([]);
  const { data, error } = await supabase.rpc('device_pull_commands', {
    p_device_id: deviceId,
    p_tenant_id: tenantId,
  });
  if (error) return err(appErrorFromSupabase(error, 'DeviceRemoteApi.pullCommands'));
  return ok((data ?? []) as PulledCommand[]);
}

export const DeviceRemoteApi = {
  async pullCommands(
    deviceId: string,
    tenantId: string,
    idle: boolean,
  ): Promise<Result<PulledCommand[], AppError>> {
    const { data, error } = await supabase.rpc('device_pull_commands', {
      p_device_id: deviceId,
      p_tenant_id: tenantId,
      p_idle: idle,
    });
    if (error?.code === FUNCTION_NOT_FOUND) return pullLegacy(deviceId, tenantId, idle);
    if (error) return err(appErrorFromSupabase(error, 'DeviceRemoteApi.pullCommands'));
    return ok((data ?? []) as PulledCommand[]);
  },

  async ackCommand(
    commandId: string,
    success: boolean,
    result: Record<string, unknown>,
  ): Promise<Result<boolean, AppError>> {
    const { data, error } = await supabase.rpc('device_ack_command', {
      p_command_id: commandId,
      p_status: success ? 'ack' : 'failed',
      p_result: result as Json,
    });
    if (error) return err(appErrorFromSupabase(error, 'DeviceRemoteApi.ackCommand'));
    return ok(data === true);
  },

  /** Commande force_logout : déconnecte CET appareil seulement, pas tout le compte. */
  async signOutThisDevice(): Promise<void> {
    await supabase.auth.signOut({ scope: 'local' });
  },

  async isAutoUpdateLocked(): Promise<Result<boolean, AppError>> {
    const { data, error } = await supabase.rpc('is_feature_locked', {
      p_key: 'updates.autoupdate',
    });
    if (error) return err(appErrorFromSupabase(error, 'DeviceRemoteApi.isAutoUpdateLocked'));
    return ok(data === true);
  },
};
